"use client";

import * as React from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Generic debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface SearchComboBoxProps<T> {
  items: T[];
  onSearchChange: (term: string) => void;
  onItemSelect: (item: T | null) => void;
  placeholder?: string;
  searchIcon?: React.ReactNode;
  clearIcon?: React.ReactNode;
  className?: string;
  capitalize?: boolean;
  getItemKey: (item: T) => string | number;
  getItemLabel: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  defaultValue?: string;
  value?: string;  // Add value prop for controlled mode
  disabled?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  debounceMs?: number;
}

export function SearchComboBox<T>({
  items,
  onSearchChange,
  onItemSelect,
  placeholder = "Search...",
  searchIcon = <Search className="h-4 w-4 text-muted-foreground" />,
  clearIcon = <X className="h-4 w-4" />,
  className,
  getItemKey,
  getItemLabel,
  renderItem,
  defaultValue = "",
  value,
  disabled = false,
  loading = false,
  capitalize = false,
  emptyMessage = "No items found",
  debounceMs = 300,
}: SearchComboBoxProps<T>) {
  const [searchTerm, setSearchTerm] = React.useState(defaultValue);
  const [selectedItem, setSelectedItem] = React.useState<T | null>(null);
  const [open, setOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  // Update searchTerm when value prop changes (controlled mode)
  React.useEffect(() => {
    if (value !== undefined) {
      setSearchTerm(value);
    }
  }, [value]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  // Memoize debounced search to prevent recreation on every render
  const debouncedSearchChange = React.useMemo(
    () =>
      debounce((value: string) => {
        onSearchChange(value);
      }, debounceMs),
    [onSearchChange, debounceMs]
  );

  // Reset highlighted index when items change
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [items]);

  // Handle item selection
  const handleSelect = React.useCallback(
    (item: T) => {
      setSelectedItem(item);
      setSearchTerm(getItemLabel(item));
      setOpen(false);
      setHighlightedIndex(-1);
      onItemSelect(item);
      inputRef.current?.blur();
    },
    [getItemLabel, onItemSelect]
  );

  // Handle clearing selection
  const handleClear = React.useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setSelectedItem(null);
      setSearchTerm("");
      setHighlightedIndex(-1);
      onItemSelect(null);
      onSearchChange(""); // Trigger search for all items
      inputRef.current?.focus(); // Focus will trigger PopoverTrigger
    },
    [onItemSelect, onSearchChange]
  );

  // Handle search input change
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      setSelectedItem(null);
      setHighlightedIndex(-1);

      // PopoverTrigger handles opening, just trigger search
      debouncedSearchChange(value);
    },
    [debouncedSearchChange]
  );

  // Handle input interactions - simplified
  const handleInputFocus = React.useCallback(() => {
    if (!disabled) {
      // PopoverTrigger handles opening, just trigger search to load data
      onSearchChange(searchTerm);
    }
  }, [disabled, searchTerm, onSearchChange]);

  // Handle keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!open) {
            // PopoverTrigger will handle opening, just trigger search
            onSearchChange(searchTerm);
          } else if (items.length > 0) {
            setHighlightedIndex((prev) =>
              prev < items.length - 1 ? prev + 1 : 0
            );
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (open && items.length > 0) {
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : items.length - 1
            );
          }
          break;
        case "Enter":
          e.preventDefault();
          if (open && highlightedIndex >= 0 && items[highlightedIndex]) {
            handleSelect(items[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setOpen(false);
          setHighlightedIndex(-1);
          inputRef.current?.blur();
          break;
        case "Tab":
          setOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    },
    [open, items, highlightedIndex, handleSelect, searchTerm, onSearchChange]
  );

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative w-full cursor-text">
            <span className="absolute left-2 top-[12px] pointer-events-none">
              {searchIcon}
            </span>
            <Input
              ref={inputRef}
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              disabled={disabled}
              className={cn("px-7 placeholder:normal-case", capitalize && "capitalize")}
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-describedby={`${placeholder}-description`}
            />
            <div className="absolute right-0 top-0 h-full flex">
              <Button
                variant="ghost"
                size="sm"
                className="h-full px-2 hover:bg-transparent"
                tabIndex={-1}
                type="button"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!open) {
                    onSearchChange(searchTerm);
                  }
                  inputRef.current?.focus();
                }}
              >
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    open && "rotate-180"
                  )}
                />
                <span className="sr-only">Toggle dropdown</span>
              </Button>
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="custom-scrollbar min-w-fit p-0"
          align="end"
          side="bottom"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {loading ? (
            <div className="px-4 py-2 text-sm text-muted-foreground flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              Loading...
            </div>
          ) : (
            <ul
              ref={listRef}
              className="max-h-[200px] overflow-y-auto py-1"
              role="listbox"
              aria-label={placeholder}
            >
              {items.length > 0 ? (
                items.map((item, index) => {
                  const itemKey = getItemKey(item);
                  // Use combination of key and index to ensure uniqueness
                  const uniqueKey = `${itemKey}-${index}`;
                  return (
                    <li
                      key={uniqueKey}
                      className={cn(
                        "px-4 py-2 cursor-pointer text-sm transition-colors",
                        "hover:bg-muted focus:bg-muted",
                        highlightedIndex === index && "bg-muted"
                      )}
                      onClick={() => handleSelect(item)}
                      role="option"
                      aria-selected={selectedItem === item}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onMouseLeave={() => setHighlightedIndex(-1)}
                    >
                      {renderItem(item)}
                    </li>
                  );
                })
              ) : (
                <li
                  className="px-4 py-2 text-muted-foreground text-sm"
                  role="option"
                  aria-disabled="true"
                >
                  {emptyMessage}
                </li>
              )}
            </ul>
          )}
        </PopoverContent>
      </Popover>
      <div id={`${placeholder}-description`} className="sr-only">
        Click or focus to see options. Type to filter, use arrow keys to
        navigate, Enter to select, Escape to close
      </div>
    </div>
  );
}
